// Synergy Matrix — stitched cross-project matrix for the aggregator's
// portfolio, per screen6-synergy.png (Story #5115 Stream 5c, Task #5122).
// Backed by Task #5119's real GET/POST /innovateflow/whitespace/portfolio_synergy
// routes. Self-contained like WhiteSpaceTab.tsx: resolves its own aggregator
// context and fetches its own data, so AggregatorPortfolio.tsx only needs to
// mount it as a tab.
//
// DATA SHAPE DIFFERS FROM THE MOCKUP (documented honestly, not silently):
// screen6 shows a DENSE grid of prior-art density counts (0-300+, same
// language as the single-project Combination Matrix). The real backend
// returns a SPARSE list of specific component pairs — only the strongest
// shared/complementary matches compute_pair_synergy found per project pair,
// each carrying a real 0-1 cosine confidence, never a prior-art count. This
// component renders that real shape: cells are shaded/badged by confidence
// (see lib/whitespace.ts's confidenceBand, reusing screen2/screen6's own
// colour palette), a combination with no real cell renders as a plain dash
// (never a fabricated 0), and the "boundary prior art" side-panel section
// says plainly that no prior-art citation endpoint exists yet rather than
// inventing patent numbers.
import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Grid3x3, Mail, FileText, RefreshCw, Loader2, AlertCircle, X } from 'lucide-react'
import {
  getPortfolioSynergy,
  getAggregatorPortfolioProjects,
  getPortfolioOverlap,
  draftPortfolioConceptNote,
  notifyPortfolioSynergyLeads,
} from '../../services/api'
import {
  buildPortfolioSynergyGrid,
  groupSynergyRowsByProject,
  confidenceBand,
  projectTagColor,
  densityBandColor,
  overlapGroupForProjectPair,
  overlapGroupMetaLine,
  portfolioProjectNames,
  type WhitespaceDensityBand,
} from '../../lib/whitespace'
import { useAggregator } from '../../contexts/AggregatorContext'
import type { PortfolioSynergyCell } from '../../types/whitespace'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import EmptyState from '../shared/EmptyState'
import HelpPopover from '../help/HelpPopover'

const BAND_LEGEND: { band: WhitespaceDensityBand; label: string }[] = [
  { band: 'white', label: '<25%' },
  { band: 'thin', label: '25–39%' },
  { band: 'light', label: '40–54%' },
  { band: 'active', label: '55–69%' },
  { band: 'crowded', label: '70–84%' },
  { band: 'saturated', label: '85–100%' },
]

function projectName(projectId: string | null, names: Map<string, string>): string {
  if (!projectId) return 'Unknown project'
  return names.get(projectId) ?? projectId
}

function cellKey(c: PortfolioSynergyCell): string {
  return `${c.feature_component_id}::${c.domain_component_id}`
}

/** Honest, data-derived narrative for the selected cell — every clause maps
 * to a real field on the cell, nothing invented (CombinationMatrix.tsx's
 * cellNarrative sets the same convention for the project-scoped matrix). */
function cellNarrative(cell: PortfolioSynergyCell, names: Map<string, string>): string {
  const featureProject = projectName(cell.feature_project_id, names)
  const domainProject = projectName(cell.domain_project_id, names)
  const pct = cell.confidence != null ? Math.round(cell.confidence * 100) : null
  const kindLabel = cell.kind === 'shared_theme' ? 'shared theme' : 'complementary pairing'
  const confidenceClause = pct != null ? `at ${pct}% component similarity` : 'with no confidence score recorded'
  return (
    `${cell.feature_title} (${featureProject}) and ${cell.domain_title} (${domainProject}) were matched as a ` +
    `${kindLabel} ${confidenceClause}. This combination only surfaces because both projects are in your portfolio — ` +
    `neither project alone would see it.`
  )
}

interface SynergyMatrixProps {
  onOpenPair?: (projectAId: string, projectBId: string) => void
  /** design §6.3 "Synergy↔Overlap reconciliation annotations + cross-links
   * both ways" — the outbound half: a cell whose two projects also form a
   * scored (>=40) Overlap group links straight into that group. Omitted
   * when the caller has nowhere to route it, rather than a dead button. */
  onOpenOverlapGroup?: (groupId: string) => void
}

export default function SynergyMatrix({ onOpenPair, onOpenOverlapGroup }: SynergyMatrixProps) {
  const { activeAggregatorId } = useAggregator()
  const qc = useQueryClient()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [isForcingRefresh, setIsForcingRefresh] = useState(false)
  // Bug #5898 (F6): column headers truncated with only a native `title`
  // hover-tooltip as the way to read the full name — slow to appear, and
  // the review never found it. A click now pins the full label instead.
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set())
  const toggleColumnExpanded = (id: string) => {
    setExpandedColumns((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const queryKey = ['portfolio-synergy', activeAggregatorId]
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => getPortfolioSynergy(activeAggregatorId),
  })

  // Shares AggregatorPortfolio's exact query key so React Query dedupes the
  // request — project names/colours for row/column headers cost no extra fetch.
  const { data: portfolioData } = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
  })

  const names = portfolioProjectNames(portfolioData)

  // Shares OverlapView's/PortfolioAtlas's exact query key — the
  // reconciliation annotation costs no extra fetch once any Overlap
  // surface has been visited (and auto-enqueues its own first compute run
  // exactly like those surfaces do if it hasn't).
  const { data: overlapData } = useQuery({
    queryKey: ['portfolio-overlap', activeAggregatorId],
    queryFn: () => getPortfolioOverlap(activeAggregatorId),
    refetchInterval: (query) => (query.state.data?.status === 'processing' ? 3000 : false),
  })
  const overlapGroups = overlapData?.groups ?? []

  const handleRefresh = async () => {
    setIsForcingRefresh(true)
    try {
      const fresh = await getPortfolioSynergy(activeAggregatorId, true)
      qc.setQueryData(queryKey, fresh)
    } catch (e) {
      setNotice((e as Error)?.message ?? 'Failed to refresh the matrix.')
    } finally {
      setIsForcingRefresh(false)
    }
  }

  const conceptNoteMutation = useMutation({
    mutationFn: (cell: PortfolioSynergyCell) => {
      const title = `${cell.feature_title} × ${cell.domain_title}`
      const pct = cell.confidence != null ? Math.round(cell.confidence * 100) : null
      const content =
        `Cross-project synergy between ${projectName(cell.feature_project_id, names)} and ` +
        `${projectName(cell.domain_project_id, names)}: ${cell.kind === 'shared_theme' ? 'shared theme' : 'complementary pairing'}` +
        (pct != null ? ` at ${pct}% component similarity` : '') +
        (cell.score != null ? `; pair synergy score ${cell.score}%.` : '.')
      return draftPortfolioConceptNote(
        cell.feature_project_id ?? '',
        cell.domain_project_id ?? '',
        title,
        content,
        activeAggregatorId,
      )
    },
    onSuccess: () => setNotice('Joint concept note drafted.'),
    onError: (e) => setNotice((e as Error)?.message ?? 'Failed to draft the concept note.'),
  })

  const notifyMutation = useMutation({
    mutationFn: (cell: PortfolioSynergyCell) =>
      notifyPortfolioSynergyLeads(cell.feature_project_id ?? '', cell.domain_project_id ?? '', activeAggregatorId),
    onSuccess: () => setNotice('Both project leads notified.'),
    onError: (e) => setNotice((e as Error)?.message ?? 'Failed to notify the project leads.'),
  })

  if (isLoading) {
    return (
      <div data-testid="synergy-matrix-loading">
        <LoadingSkeleton type="card" rows={3} />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-4 text-sm text-red-600" role="alert" data-testid="synergy-matrix-error">
        Couldn't load the synergy matrix: {(error as Error)?.message ?? 'unknown error'}
      </div>
    )
  }

  const memberProjectIds = data?.member_project_ids ?? []
  const cells = data?.cells ?? []

  if (memberProjectIds.length < 2) {
    return (
      <EmptyState
        icon={<Grid3x3 className="w-7 h-7" />}
        title="Not enough projects for a synergy matrix"
        description="Your portfolio needs at least two entitled projects before cross-project synergy can be scored."
        action={{ label: 'Refresh', onClick: handleRefresh }}
      />
    )
  }

  if (cells.length === 0) {
    return (
      <EmptyState
        icon={<Grid3x3 className="w-7 h-7" />}
        title="No shared or complementary components found yet"
        description="No strong shared themes or complementary pairings were found across this portfolio's projects."
        action={{ label: 'Refresh', onClick: handleRefresh }}
      />
    )
  }

  const { features, domains, grid } = buildPortfolioSynergyGrid(cells)
  const rowGroups = groupSynergyRowsByProject(features)
  const selected = selectedKey ? cells.find((c) => cellKey(c) === selectedKey) ?? null : null
  const selectedReconciledGroup = selected
    ? overlapGroupForProjectPair(overlapGroups, selected.feature_project_id, selected.domain_project_id)
    : null
  const truncatedCount = data?.truncated_member_project_ids.length ?? 0

  return (
    <div data-testid="synergy-matrix">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <h2 className="text-sm font-semibold text-iflow-900 flex items-center gap-1.5">
          <Grid3x3 className="w-4 h-4" /> Synergy Matrix
        </h2>
        <HelpPopover topic="pf-synergy" size={14} />
        <span className="px-2 py-0.5 text-[10.5px] rounded-full bg-violet-500/10 text-violet-700 font-medium">
          ◆ = cross-project synergy cell
        </span>
        <div className="flex-1" />
        <button
          onClick={handleRefresh}
          disabled={isForcingRefresh || isFetching}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isForcingRefresh ? 'animate-spin' : ''}`} />
          {isForcingRefresh ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <p className="text-[11.5px] text-gray-600 mb-3">
        Rows are inventive features scored across your portfolio's project pairs; columns are the components those
        features were compared against. A ◆ badge marks a synergy cell — the feature and the component it's compared
        with come from different projects, so the combination is only visible inside this portfolio.
      </p>

      {truncatedCount > 0 && (() => {
        // Bug #5898 (F7): the exclusion notice carried the same small,
        // easy-to-miss weight whether it excluded 1 project or 34 of 42 — a
        // majority-excluded matrix needs to say so loudly, not in a banner
        // no bigger than a dismissible toast.
        const totalCount = memberProjectIds.length + truncatedCount
        const majorityExcluded = totalCount > 0 && truncatedCount / totalCount >= 0.5
        return (
          <div
            className={
              majorityExcluded
                ? 'mb-3 p-3 bg-red-50 border-2 border-red-300 text-red-900 text-sm font-semibold rounded-lg'
                : 'mb-3 p-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg'
            }
            role="status"
          >
            {majorityExcluded
              ? `Most of this portfolio is NOT shown here: ${truncatedCount} of ${totalCount} projects ` +
                `(${Math.round((truncatedCount / totalCount) * 100)}%) are excluded — the synergy matrix ` +
                `covers only 8 projects at a time.`
              : `${truncatedCount} project${truncatedCount === 1 ? '' : 's'} excluded from the matrix — the ` +
                `portfolio synergy cap is 8 projects.`}
          </div>
        )
      })()}

      {notice && (
        <div className="mb-3 p-2 bg-sky-50 border border-sky-200 text-iflow-700 text-xs rounded-lg flex items-center justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex gap-4 flex-col lg:flex-row">
        <div className="light-card p-4 flex-1 overflow-x-auto">
          <table className="border-separate" style={{ borderSpacing: 5 }} role="grid" aria-label="Portfolio synergy matrix">
            <thead>
              <tr>
                <th />
                {domains.map((d) => {
                  const color = projectTagColor(d.project_id, memberProjectIds)
                  const isExpanded = expandedColumns.has(d.id)
                  return (
                    <th
                      key={d.id}
                      className={`text-[10.5px] font-semibold text-gray-600 text-left align-bottom px-1 ${isExpanded ? 'max-w-[220px]' : 'max-w-[90px]'}`}
                      title={`${d.title} — ${projectName(d.project_id, names)}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleColumnExpanded(d.id)}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} column header: ${d.title}`}
                        className="block w-full text-left hover:underline"
                      >
                        <span className={isExpanded ? 'block whitespace-normal break-words max-w-[216px]' : 'block truncate max-w-[86px]'}>
                          {d.title}
                        </span>
                      </button>
                      <span
                        className="inline-block w-2 h-2 rounded-sm mt-1"
                        style={{ background: color }}
                        aria-hidden
                      />
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rowGroups.map((group) => (
                <Fragment key={`group-${group.project_id ?? 'unknown'}`}>
                  <tr>
                    <th colSpan={domains.length + 1} className="text-left pt-2 pb-0.5">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-[-1px]"
                        style={{ background: projectTagColor(group.project_id, memberProjectIds) }}
                        aria-hidden
                      />
                      <span className="text-[11px] font-bold text-iflow-900">
                        {projectName(group.project_id, names)}
                      </span>
                    </th>
                  </tr>
                  {group.rowIndices.map((fi) => (
                    <tr key={features[fi].id}>
                      <th
                        className="text-[11px] font-semibold text-gray-700 text-left pr-2 whitespace-nowrap max-w-[160px] truncate"
                        title={features[fi].title}
                      >
                        {features[fi].title}
                      </th>
                      {domains.map((d, di) => {
                        const cell = grid[fi][di]
                        if (!cell) {
                          return (
                            <td key={d.id} className="w-24 h-11 text-center text-gray-300 text-[11px]" aria-label="No data for this combination">
                              –
                            </td>
                          )
                        }
                        const band = confidenceBand(cell.confidence)
                        const colors = densityBandColor(band)
                        const key = cellKey(cell)
                        const pct = cell.confidence != null ? Math.round(cell.confidence * 100) : 0
                        const reconciledGroup = overlapGroupForProjectPair(
                          overlapGroups, cell.feature_project_id, cell.domain_project_id,
                        )
                        return (
                          <td key={d.id} className="p-0">
                            <button
                              type="button"
                              role="gridcell"
                              aria-label={`${cell.feature_title} × ${cell.domain_title}: ${pct}% (${band})${cell.marker ? ', cross-project synergy cell' : ''}${reconciledGroup ? ', also a scored Overlap group' : ''}`}
                              aria-pressed={selectedKey === key}
                              onClick={() => setSelectedKey(key)}
                              className="relative w-24 h-11 rounded-lg text-center text-[11px] font-bold flex flex-col items-center justify-center py-1"
                              style={{
                                background: colors.background,
                                color: colors.color,
                                border: reconciledGroup ? '1.8px solid #D97706' : band === 'white' ? '1.8px dashed #D97706' : 'none',
                                outline: selectedKey === key ? '3px solid #FBBF24' : undefined,
                                outlineOffset: 1,
                              }}
                            >
                              {cell.marker && (
                                <span
                                  className="absolute -top-1.5 -right-1 bg-violet-600 text-white text-[8.5px] font-black rounded-full px-1 border border-white"
                                  aria-hidden
                                >
                                  {cell.marker}
                                </span>
                              )}
                              {reconciledGroup && (
                                <span
                                  className="absolute -bottom-1.5 -left-1 bg-amber-500 text-white text-[8.5px] font-black rounded-full px-1 border border-white"
                                  aria-hidden
                                  title="Also a scored Overlap group"
                                >
                                  ⬡
                                </span>
                              )}
                              <span className="block text-[12.5px] font-extrabold">{pct}%</span>
                            </button>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>

          <div className="flex items-center gap-2 flex-wrap mt-3 text-[11px] text-gray-500">
            Synergy confidence:
            {BAND_LEGEND.map(({ band, label }) => {
              const colors = densityBandColor(band)
              return (
                <span key={band} className="flex items-center gap-1">
                  <i
                    className="inline-block w-[26px] h-[10px] rounded-sm"
                    style={{ background: colors.background, border: band === 'white' ? '1.5px dashed #D97706' : 'none' }}
                  />
                  {label}
                </span>
              )
            })}
            <span className="ml-2 text-violet-700 font-semibold">
              ◆ {data?.cross_project_cell_count ?? 0} synergy cells
            </span>
          </div>
        </div>

        <div className="w-full lg:w-[300px] flex flex-col gap-3">
          <div className="light-card p-4" data-testid="synergy-matrix-cell-detail">
            {selected ? (
              <>
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center w-11 h-11 rounded-full bg-amber-400 text-gray-900 text-[15px] font-extrabold flex-shrink-0">
                    {selected.confidence != null ? Math.round(selected.confidence * 100) : '–'}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13px] font-extrabold text-iflow-900 truncate">
                      {selected.feature_title} × {selected.domain_title}
                    </div>
                    <div className="text-[10.5px] text-gray-500">
                      <span style={{ color: projectTagColor(selected.feature_project_id, memberProjectIds) }} className="font-semibold">
                        {projectName(selected.feature_project_id, names)}
                      </span>{' '}
                      feature ×{' '}
                      <span style={{ color: projectTagColor(selected.domain_project_id, memberProjectIds) }} className="font-semibold">
                        {projectName(selected.domain_project_id, names)}
                      </span>{' '}
                      component
                    </div>
                  </div>
                </div>

                <p className="text-[11.5px] text-gray-700 mt-2.5 leading-relaxed">{cellNarrative(selected, names)}</p>

                {selected.is_cross_project && (
                  <div className="rounded-lg bg-violet-50 border border-violet-200 px-2.5 py-2 mt-2 text-[11px] text-violet-800 font-medium">
                    ◆ Synergy cell — this combination is invisible to anyone who sees only one of the two projects.
                  </div>
                )}

                {/* design §6.3 reconciliation — this project pair also forms
                    a scored (>=40) Overlap group; cross-link both readings. */}
                {selectedReconciledGroup && (
                  <div
                    className="rounded-lg bg-amber-50 border border-amber-300 px-2.5 py-2 mt-2 text-[11px] text-amber-800 font-medium"
                    data-testid="synergy-overlap-reconciliation"
                  >
                    ⬡ Also a scored Overlap group — {overlapGroupMetaLine(selectedReconciledGroup)}.
                    {onOpenOverlapGroup && (
                      <button
                        onClick={() => onOpenOverlapGroup(selectedReconciledGroup.group_id)}
                        className="ml-1.5 text-amber-900 font-semibold hover:underline"
                      >
                        Open in Overlap →
                      </button>
                    )}
                  </div>
                )}

                <div className="mt-2.5">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                    Boundary prior art
                  </label>
                  <p className="text-[10.5px] text-gray-500 mt-1 leading-relaxed">
                    Not available yet — the synergy endpoint returns component match confidence, not nearest
                    prior-art citations. Use the pair synergy score
                    {selected.score != null ? ` (${selected.score}%)` : ''} above as the closest real signal.
                  </p>
                </div>

                <div className="flex gap-2 mt-3 flex-wrap">
                  <button
                    onClick={() => conceptNoteMutation.mutate(selected)}
                    disabled={conceptNoteMutation.isPending}
                    className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-amber-400 hover:bg-amber-300 text-gray-900 rounded-md transition-colors disabled:opacity-50"
                  >
                    {conceptNoteMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                    Draft joint concept note
                  </button>
                  <button
                    onClick={() => notifyMutation.mutate(selected)}
                    disabled={notifyMutation.isPending}
                    className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-md transition-colors disabled:opacity-50"
                  >
                    {notifyMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                    Notify both leads
                  </button>
                  {onOpenPair && (
                    <button
                      onClick={() => onOpenPair(selected.feature_project_id ?? '', selected.domain_project_id ?? '')}
                      className="px-2.5 py-1 text-[11px] font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-md transition-colors"
                    >
                      Open pair
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-500 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> Select a cell to see its detail.
              </p>
            )}
          </div>
          <div className="light-card p-4">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              How rows &amp; columns are stitched
            </div>
            <p className="text-[11.3px] text-gray-700 mt-1.5 leading-relaxed">
              Rows and columns are the specific components we matched across every pair of
              projects in your portfolio — up to the top 5 shared themes and top 3 complementary areas
              per pair. A combination with no cell here was never scored, not scored at zero.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
